import { RESOURCE_BY_SKILL } from './catalog.ts';
import {
  BASE_RESOURCE_PER_SECOND,
  BASE_XP_PER_SECOND,
  beyondTheVeilMultiplier,
  DEEP_SKILL_LEVEL,
  globalProductionMultiplier,
  globalXpMultiplier,
  manualActionMultiplier,
  levelFromXp,
  MAX_SKILL_LEVEL,
  MAX_SKILL_XP,
  offlineCapSeconds,
  offlineEfficiency,
  skillMultiplier,
  twinEchoEfficiency,
  xpForLevel
} from './balance.ts';
import { recordProgress } from './progress.ts';
import {
  activitiesFor, advanceRealmEvents, currentProductionSurgeSeconds, PRODUCTION_SURGE_MULTIPLIER
} from './activities.ts';
import type { CriticalTrainingEvent, GameState, SimulationOptions, SimulationResult, SkillState } from './types.ts';
import { constellationCriticalChance, constellationPerfectStrikeMultiplier } from './constellation.ts';

export const MANUAL_ACTION_XP = 2;
export const MANUAL_ACTION_RESOURCE = 1;
export const CRITICAL_TRAINING_CHANCE = 0.02;

export interface ManualActionResult {
  state: GameState;
  gainedXp: number;
  gainedResource: number;
  resource: string;
  criticalEvents?: CriticalTrainingEvent[];
}

interface SkillAdvance {
  skill: SkillState;
  gainedXp: number;
  gainedResource: number;
}

function advanceSkillExactly(
  skill: SkillState,
  seconds: number,
  xpRate: number,
  productionMultiplier: number,
  deepXpMultiplier: number
): SkillAdvance {
  let remaining = Math.max(0, seconds);
  let xp = skill.xp;
  let level = skill.level;
  let resource = 0;

  while (remaining > 0 && level < MAX_SKILL_LEVEL) {
    const nextLevelXp = xpForLevel(level + 1);
    const segmentXpRate = xpRate * (level >= DEEP_SKILL_LEVEL ? deepXpMultiplier : 1);
    const secondsToLevel = segmentXpRate > 0 ? Math.max(0, (nextLevelXp - xp) / segmentXpRate) : Number.POSITIVE_INFINITY;
    const segment = Math.min(remaining, secondsToLevel || remaining);
    resource += segment * BASE_RESOURCE_PER_SECOND * (1 + level / 10) * productionMultiplier;
    xp = Math.min(MAX_SKILL_XP, xp + segment * segmentXpRate);
    remaining -= segment;
    const nextLevel = levelFromXp(xp);
    if (nextLevel === level && segment === 0) break;
    level = nextLevel;
  }

  if (remaining > 0) {
    resource += remaining * BASE_RESOURCE_PER_SECOND * (1 + level / 10) * productionMultiplier;
    const segmentXpRate = xpRate * (level >= DEEP_SKILL_LEVEL ? deepXpMultiplier : 1);
    xp = Math.min(MAX_SKILL_XP, xp + remaining * segmentXpRate);
  }

  return {
    skill: { ...skill, xp, level: levelFromXp(xp) },
    gainedXp: xp - skill.xp,
    gainedResource: resource
  };
}

function deterministicUnit(seed: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return (hash >>> 0) / 4_294_967_296;
}

function nextCriticalDelay(state: GameState, sequence: number): number {
  const roll = Math.min(1 - Number.EPSILON, deterministicUnit(`${state.playerId}:${state.run.startedAt}:${sequence}:critical-gap`));
  return Math.max(1, Math.ceil(Math.log(1 - roll) / Math.log(1 - constellationCriticalChance(state))));
}

function criticalKind(state: GameState, sequence: number): CriticalTrainingEvent['kind'] {
  const roll = deterministicUnit(`${state.playerId}:${state.run.startedAt}:${sequence}:critical-kind`);
  return roll < 0.4 ? 'harvest' : roll < 0.75 ? 'insight' : 'strike';
}

export function simulate(state: GameState, targetTime: number, options: SimulationOptions): SimulationResult {
  const simulationTime = Math.max(state.lastSimulatedAt, targetTime);
  const requestedSeconds = Math.max(0, (simulationTime - state.lastSimulatedAt) / 1000);
  if (requestedSeconds === 0) {
    return {
      state,
      requestedSeconds: 0,
      simulatedSeconds: 0,
      cappedSeconds: 0,
      gainedXp: 0,
      gainedResources: {}
    };
  }
  const cap = options.mode === 'offline' ? offlineCapSeconds(state) : requestedSeconds;
  const simulatedSeconds = Math.min(requestedSeconds, cap);
  const cappedSeconds = Math.max(0, requestedSeconds - simulatedSeconds);
  const efficiency = options.mode === 'offline' ? offlineEfficiency(state) : 1;
  if (state.run.trainingActive === false) {
    const waitingState: GameState = {
      ...state,
      updatedAt: simulationTime,
      lastSimulatedAt: simulationTime,
      revision: state.revision + 1,
      stats: {
        ...state.stats,
        activeSeconds: state.stats.activeSeconds + (options.mode === 'active' ? simulatedSeconds : 0),
        offlineSeconds: state.stats.offlineSeconds + (options.mode === 'offline' ? simulatedSeconds : 0)
      }
    };
    return {
      state: recordProgress(advanceRealmEvents(waitingState, simulationTime, options.mode, requestedSeconds)).state,
      requestedSeconds,
      simulatedSeconds,
      cappedSeconds,
      gainedXp: 0,
      gainedResources: {}
    };
  }
  const active = state.run.skills[state.run.activeSkill];
  const skillBoost = skillMultiplier(active);
  const xpMultiplier = skillBoost * globalXpMultiplier(state, active.id, options.mode) * efficiency;
  const productionMultiplier = skillBoost * globalProductionMultiplier(state, active.id, options.mode) * efficiency;
  const activities = activitiesFor(state);
  const boostedSeconds = Math.min(simulatedSeconds, activities.productionBoostSeconds);
  const normalSeconds = simulatedSeconds - boostedSeconds;
  let advancedSkill = active;
  let gainedXp = 0;
  let gainedResource = 0;
  let gainedGold = 0;
  const criticalEvents: CriticalTrainingEvent[] = [];
  let criticalSequence = state.run.criticalSequence ?? 0;
  let criticalSecondsUntilProc = state.run.criticalSecondsUntilProc ?? nextCriticalDelay(state, criticalSequence);

  const advanceSpan = (start: number, end: number): void => {
    let cursor = start;
    while (cursor < end) {
      const boosted = cursor < boostedSeconds;
      const boundary = boosted ? Math.min(end, boostedSeconds) : end;
      const multiplier = boosted ? PRODUCTION_SURGE_MULTIPLIER : 1;
      const result = advanceSkillExactly(
        advancedSkill,
        boundary - cursor,
        BASE_XP_PER_SECOND * xpMultiplier * multiplier,
        productionMultiplier * multiplier,
        beyondTheVeilMultiplier(state)
      );
      advancedSkill = result.skill;
      gainedXp += result.gainedXp;
      gainedResource += result.gainedResource;
      cursor = boundary;
    }
  };

  let elapsed = 0;
  const criticalsEnabled = options.mode === 'active' && options.criticalTraining !== false;
  if (criticalsEnabled) {
    while (elapsed + criticalSecondsUntilProc <= simulatedSeconds) {
      const procAt = elapsed + criticalSecondsUntilProc;
      advanceSpan(elapsed, procAt);
      const surgeMultiplier = procAt <= boostedSeconds ? PRODUCTION_SURGE_MULTIPLIER : 1;
      const sample = advanceSkillExactly(
        advancedSkill,
        1,
        BASE_XP_PER_SECOND * xpMultiplier * surgeMultiplier,
        productionMultiplier * surgeMultiplier,
        beyondTheVeilMultiplier(state)
      );
      const kind = criticalKind(state, criticalSequence);
      if (kind === 'harvest') {
        const amount = sample.gainedResource * 7;
        gainedResource += amount;
        criticalEvents.push({ kind, skill: active.id, resource: RESOURCE_BY_SKILL[active.id], amount });
      } else if (kind === 'insight') {
        const amount = Math.min(MAX_SKILL_XP - advancedSkill.xp, sample.gainedXp * 4);
        const xp = advancedSkill.xp + amount;
        advancedSkill = { ...advancedSkill, xp, level: levelFromXp(xp) };
        gainedXp += amount;
        criticalEvents.push({ kind, skill: active.id, amount });
      } else {
        const amount = Math.max(1, Math.ceil(sample.gainedResource * 5 * constellationPerfectStrikeMultiplier(state)));
        gainedGold += amount;
        criticalEvents.push({ kind, skill: active.id, amount });
      }
      elapsed = procAt;
      criticalSequence += 1;
      criticalSecondsUntilProc = nextCriticalDelay(state, criticalSequence);
    }
    criticalSecondsUntilProc -= simulatedSeconds - elapsed;
  }
  advanceSpan(elapsed, simulatedSeconds);
  const advanced = { skill: advancedSkill, gainedXp, gainedResource };
  const resource = RESOURCE_BY_SKILL[active.id];
  const gainedResources: Record<string, number> = advanced.gainedResource > 0 ? { [resource]: advanced.gainedResource } : {};
  const passiveEfficiency = twinEchoEfficiency(state);
  if (passiveEfficiency > 0) {
    for (const skillId of state.prestige.passiveSkills ?? []) {
      if (skillId === active.id) continue;
      const passive = state.run.skills[skillId];
      if (!passive) continue;
      const passiveResource = RESOURCE_BY_SKILL[skillId];
      const effectiveSeconds = boostedSeconds * PRODUCTION_SURGE_MULTIPLIER + normalSeconds;
      const gain = effectiveSeconds
        * BASE_RESOURCE_PER_SECOND
        * (1 + passive.level / 10)
        * skillMultiplier(passive)
        * globalProductionMultiplier(state, skillId, options.mode)
        * efficiency
        * passiveEfficiency;
      gainedResources[passiveResource] = (gainedResources[passiveResource] ?? 0) + gain;
    }
  }
  const nextResources = { ...state.run.resources };
  const nextEarnedResources = { ...state.run.earnedResources };
  for (const [gainedResource, amount] of Object.entries(gainedResources)) {
    nextResources[gainedResource] = (nextResources[gainedResource] ?? 0) + amount;
    nextEarnedResources[gainedResource] = (nextEarnedResources[gainedResource] ?? 0) + amount;
  }
  const totalGainedResource = Object.values(gainedResources).reduce((sum, amount) => sum + amount, 0);
  const gainedLevels = Math.max(0, advanced.skill.level - active.level);
  const lifetimeResources = { ...(state.stats.resourceUnitsEarned ?? {}) };
  for (const [gainedResource, amount] of Object.entries(gainedResources)) {
    lifetimeResources[gainedResource] = (lifetimeResources[gainedResource] ?? 0) + amount;
  }
  const nextState: GameState = {
    ...state,
    updatedAt: simulationTime,
    lastSimulatedAt: simulationTime,
    revision: state.revision + 1,
    run: {
      ...state.run,
      gold: state.run.gold + gainedGold,
      earnedGold: state.run.earnedGold + gainedGold,
      criticalSequence: criticalsEnabled ? criticalSequence : state.run.criticalSequence,
      criticalSecondsUntilProc: criticalsEnabled ? criticalSecondsUntilProc : state.run.criticalSecondsUntilProc,
      resources: nextResources,
      earnedResources: nextEarnedResources,
      productionValue: state.run.productionValue + totalGainedResource,
      skills: {
        ...state.run.skills,
        [active.id]: advanced.skill
      }
    },
    prestige: {
      ...state.prestige,
      activities: { ...activities, productionBoostSeconds: Math.max(0, activities.productionBoostSeconds - simulatedSeconds) }
    },
    stats: {
      ...state.stats,
      activeSeconds: state.stats.activeSeconds + (options.mode === 'active' ? simulatedSeconds : 0),
      offlineSeconds: state.stats.offlineSeconds + (options.mode === 'offline' ? simulatedSeconds : 0),
      totalResourceUnitsEarned: state.stats.totalResourceUnitsEarned + totalGainedResource,
      totalGoldEarned: state.stats.totalGoldEarned + gainedGold,
      skillLevelsGained: (state.stats.skillLevelsGained ?? 0) + gainedLevels,
      criticalTrainingProcs: (state.stats.criticalTrainingProcs ?? 0) + criticalEvents.length,
      skillXpEarned: {
        ...(state.stats.skillXpEarned ?? {}),
        [active.id]: (state.stats.skillXpEarned?.[active.id] ?? 0) + advanced.gainedXp
      },
      activeSecondsBySkill: {
        ...(state.stats.activeSecondsBySkill ?? {}),
        [active.id]: (state.stats.activeSecondsBySkill?.[active.id] ?? 0) + (options.mode === 'active' ? simulatedSeconds : 0)
      },
      resourceUnitsEarned: lifetimeResources,
      skillLevelsGainedBySkill: {
        ...(state.stats.skillLevelsGainedBySkill ?? {}),
        [active.id]: (state.stats.skillLevelsGainedBySkill?.[active.id] ?? 0) + gainedLevels
      },
      surgeTrainingSeconds: (state.stats.surgeTrainingSeconds ?? 0) + boostedSeconds
    }
  };

  return {
    state: recordProgress(advanceRealmEvents(nextState, simulationTime, options.mode, requestedSeconds)).state,
    requestedSeconds,
    simulatedSeconds,
    cappedSeconds,
    gainedXp: advanced.gainedXp,
    gainedResources,
    gainedGold,
    criticalEvents
  };
}

export function performManualAction(state: GameState, now = Date.now()): ManualActionResult {
  const simulation = simulate(state, now, { mode: 'active' });
  const current = simulation.state;
  const active = current.run.skills[current.run.activeSkill];
  const resource = RESOURCE_BY_SKILL[active.id];
  if (current.run.trainingActive === false) {
    return { state: current, gainedXp: 0, gainedResource: 0, resource, criticalEvents: simulation.criticalEvents };
  }
  const manualMultiplier = manualActionMultiplier(current);
  const surgeMultiplier = currentProductionSurgeSeconds(current) > 0 ? PRODUCTION_SURGE_MULTIPLIER : 1;
  const deepXpMultiplier = active.level >= DEEP_SKILL_LEVEL ? beyondTheVeilMultiplier(current) : 1;
  const gainedXp = MANUAL_ACTION_XP * globalXpMultiplier(current) * manualMultiplier * deepXpMultiplier * surgeMultiplier;
  const gainedResource = MANUAL_ACTION_RESOURCE
    * skillMultiplier(active)
    * globalProductionMultiplier(current)
    * manualMultiplier
    * surgeMultiplier;
  const xp = Math.min(MAX_SKILL_XP, active.xp + gainedXp);
  const gainedLevels = Math.max(0, levelFromXp(xp) - active.level);

  const nextState: GameState = {
    ...current,
    updatedAt: now,
    revision: current.revision + 1,
    run: {
      ...current.run,
      resources: {
        ...current.run.resources,
        [resource]: (current.run.resources[resource] ?? 0) + gainedResource
      },
      earnedResources: {
        ...current.run.earnedResources,
        [resource]: (current.run.earnedResources[resource] ?? 0) + gainedResource
      },
      productionValue: current.run.productionValue + gainedResource,
      skills: {
        ...current.run.skills,
        [active.id]: { ...active, xp, level: levelFromXp(xp) }
      }
    },
    stats: {
      ...current.stats,
      totalResourceUnitsEarned: current.stats.totalResourceUnitsEarned + gainedResource,
      manualActions: (current.stats.manualActions ?? 0) + 1,
      skillLevelsGained: (current.stats.skillLevelsGained ?? 0) + gainedLevels,
      skillXpEarned: {
        ...(current.stats.skillXpEarned ?? {}),
        [active.id]: (current.stats.skillXpEarned?.[active.id] ?? 0) + gainedXp
      },
      resourceUnitsEarned: {
        ...(current.stats.resourceUnitsEarned ?? {}),
        [resource]: (current.stats.resourceUnitsEarned?.[resource] ?? 0) + gainedResource
      },
      skillLevelsGainedBySkill: {
        ...(current.stats.skillLevelsGainedBySkill ?? {}),
        [active.id]: (current.stats.skillLevelsGainedBySkill?.[active.id] ?? 0) + gainedLevels
      }
    }
  };

  return {
    gainedXp,
    gainedResource,
    resource,
    criticalEvents: simulation.criticalEvents,
    state: recordProgress(nextState).state
  };
}
